/**
 * Reconciliation Session Page
 *
 * Main reconciliation view with split layout:
 * - Left: CSV lines from bank statement
 * - Right: Matching operations from account
 *
 * Features:
 * - CSV file upload and parsing
 * - Automatic matching suggestions
 * - Manual matching
 * - Operation creation from CSV line
 * - Tolerance adjustment
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Modal } from '../components/ui';
import { ReconciliationImportWizard, PDFImportPanel, type ParsedReconciliationLine } from '../components/reconciliation';
import { reconciliationService } from '../services/reconciliation.service';
import { useAccountStore } from '../store/accountStore';
import { formatDate, formatCurrency } from '../utils/formatters';
import type {
  ReconciliationSession,
  ReconciliationLine,
  ReconciliationMatch,
  Operation,
  PDFExtractionStatus,
  PDFExtractedTransaction,
} from '@budgira/shared';

// Status colors for lines
const LINE_STATUS_COLORS = {
  pending: 'border-l-yellow-400 bg-yellow-50 dark:bg-yellow-900/10',
  matched: 'border-l-green-400 bg-green-50 dark:bg-green-900/10',
  created: 'border-l-blue-400 bg-blue-50 dark:bg-blue-900/10',
  ignored: 'border-l-gray-400 bg-gray-50 dark:bg-gray-800/50',
};

const ReconciliationSessionPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { accounts, fetchAccounts } = useAccountStore();

  // Data state
  const [session, setSession] = useState<ReconciliationSession | null>(null);
  const [lines, setLines] = useState<ReconciliationLine[]>([]);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [matches, setMatches] = useState<Record<string, ReconciliationMatch[]>>({});

  // Refs for scrolling to operations
  const operationRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  
  // PDF import state
  const [showPDFImport, setShowPDFImport] = useState(false);
  const [pdfExtractionStatus, setPdfExtractionStatus] = useState<PDFExtractionStatus | null>(null);

  // Settings modal
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [newTolerance, setNewTolerance] = useState(3);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Complete session modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // Date adjustment modal
  const [showDateAdjustModal, setShowDateAdjustModal] = useState(false);
  const [pendingMatch, setPendingMatch] = useState<{
    lineId: string;
    operationId: string;
    lineDate: string;
    operationDate: string;
    // Counterparty info for alias suggestion
    lineDescription: string;
    counterpartyName?: string;
    counterpartyId?: string;
  } | null>(null);
  const [shouldUpdateDate, setShouldUpdateDate] = useState(false);
  const [shouldCreateAlias, setShouldCreateAlias] = useState(false);

  // Line filter
  const [lineFilter, setLineFilter] = useState<'all' | 'pending' | 'with-suggestion' | 'matched' | 'created' | 'ignored'>('all');
  const [lineSearchQuery, setLineSearchQuery] = useState('');

  // Operation filter
  const [operationFilter, setOperationFilter] = useState<'all' | 'reconciled' | 'not-reconciled'>('all');

  // Operation details modal
  const [showOperationDetails, setShowOperationDetails] = useState(false);
  const [selectedOperationForDetails, setSelectedOperationForDetails] = useState<Operation | null>(null);

  // Mobile view tab
  const [mobileTab, setMobileTab] = useState<'lines' | 'operations'>('lines');

  // Fetch session data
  const fetchSessionData = useCallback(async () => {
    if (!sessionId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await reconciliationService.getSessionWithData(sessionId);
      setSession(data.session);
      setLines(data.lines);
      setOperations(data.operations);
      setMatches(data.matches);
      setNewTolerance(data.session.dateToleranceDays);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch session');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchAccounts();
    fetchSessionData();
    // Check PDF extraction availability
    reconciliationService.getPDFExtractionStatus()
      .then(setPdfExtractionStatus)
      .catch(() => setPdfExtractionStatus({ available: false, supportedProviders: [] }));
  }, [fetchAccounts, fetchSessionData]);

  // Scroll to best match when a line is selected
  useEffect(() => {
    if (selectedLineId) {
      const lineMatches = matches[selectedLineId];
      if (lineMatches && lineMatches.length > 0) {
        const bestMatch = lineMatches[0];
        if (bestMatch) {
          const operationEl = operationRefs.current[bestMatch.operationId];
          if (operationEl) {
            // Small delay to let the UI update
            setTimeout(() => {
              operationEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
          }
        }
      }
    }
  }, [selectedLineId, matches]);

  // Get account name
  const accountName = session ? accounts.find((a) => a.id === session.accountId)?.name : '';

  // Handle import from wizard
  const handleImportLines = useCallback(async (parsedLines: ParsedReconciliationLine[]) => {
    if (!sessionId) return;

    setIsImporting(true);
    setError(null);

    try {
      const linesToImport = parsedLines.map((line) => ({
        lineNumber: line.lineNumber,
        date: line.date,
        description: line.description,
        amount: line.amount,
      }));

      await reconciliationService.importLines(sessionId, linesToImport);
      await fetchSessionData();
      setShowImportWizard(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import file');
      throw err; // Re-throw to let wizard handle the error
    } finally {
      setIsImporting(false);
    }
  }, [sessionId, fetchSessionData]);

  // Handle import from PDF
  const handleImportPDFLines = useCallback(async (pdfLines: PDFExtractedTransaction[]) => {
    if (!sessionId) return;

    setIsImporting(true);
    setError(null);

    try {
      const linesToImport = pdfLines.map((line) => ({
        lineNumber: line.lineNumber,
        date: line.date,
        description: line.description,
        amount: line.amount,
      }));

      await reconciliationService.importLines(sessionId, linesToImport);
      await fetchSessionData();
      setShowPDFImport(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import PDF');
      throw err;
    } finally {
      setIsImporting(false);
    }
  }, [sessionId, fetchSessionData]);

  // Match a line with an operation
  const handleMatch = async (lineId: string, operationId: string, updateDate: boolean = false, counterpartyAlias?: string) => {
    if (!sessionId) return;

    try {
      await reconciliationService.matchLine(sessionId, lineId, operationId, true, updateDate, counterpartyAlias);
      await fetchSessionData();
      setSelectedLineId(null);
      setShowDateAdjustModal(false);
      setPendingMatch(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to match');
    }
  };

  // Check if dates differ or counterparty differs and show modal, or match directly
  const initiateMatch = (lineId: string, operationId: string) => {
    const line = lines.find(l => l.id === lineId);
    const operation = operations.find(o => o.id === operationId);
    
    if (!line || !operation) return;
    
    // Compare dates (normalize to YYYY-MM-DD)
    const lineDate = line.date.split('T')[0];
    const opDate = operation.paymentDate.split('T')[0];
    
    // Check if counterparty differs from line description
    const counterpartyName = operation.counterparty?.name;
    const counterpartyId = operation.counterpartyId;
    const lineDescription = line.description;
    
    // Consider counterparty different if:
    // - Operation has a counterparty AND
    // - Line description doesn't contain the counterparty name (case-insensitive)
    const hasCounterpartyMismatch = counterpartyName && counterpartyId &&
      !lineDescription.toLowerCase().includes(counterpartyName.toLowerCase());
    
    if (lineDate !== opDate || hasCounterpartyMismatch) {
      // Show confirmation modal for date and/or alias
      setPendingMatch({
        lineId,
        operationId,
        lineDate: line.date,
        operationDate: operation.paymentDate,
        lineDescription,
        counterpartyName,
        counterpartyId,
      });
      // Pre-check options by default
      setShouldUpdateDate(lineDate !== opDate);
      setShouldCreateAlias(!!hasCounterpartyMismatch);
      setShowDateAdjustModal(true);
    } else {
      // Everything matches - proceed directly
      handleMatch(lineId, operationId, false);
    }
  };

  // Ignore a line
  const handleIgnore = async (lineId: string) => {
    if (!sessionId) return;

    try {
      await reconciliationService.ignoreLine(sessionId, lineId);
      await fetchSessionData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to ignore line');
    }
  };

  // Reset a line
  const handleReset = async (lineId: string) => {
    if (!sessionId) return;

    try {
      await reconciliationService.resetLine(sessionId, lineId);
      await fetchSessionData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset line');
    }
  };

  // Update tolerance
  const handleSaveTolerance = async () => {
    if (!sessionId) return;

    try {
      setIsSavingSettings(true);
      await reconciliationService.updateTolerance(sessionId, newTolerance);
      await fetchSessionData();
      setShowSettingsModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tolerance');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Complete session
  const handleCompleteSession = async () => {
    if (!sessionId) return;

    try {
      setIsCompleting(true);
      await reconciliationService.completeSession(sessionId);
      setShowCompleteModal(false);
      navigate('/reconciliation');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete session');
    } finally {
      setIsCompleting(false);
    }
  };

  // Get selected line
  const selectedLine = selectedLineId ? lines.find((l) => l.id === selectedLineId) : null;

  // Get matches for selected line
  const selectedLineMatches = selectedLineId ? matches[selectedLineId] || [] : [];

  // Filter lines based on selected filter and search query
  const filteredLines = lines.filter((line) => {
    // First apply search filter
    if (lineSearchQuery.trim()) {
      const query = lineSearchQuery.toLowerCase().trim();
      const descriptionMatch = line.description.toLowerCase().includes(query);
      if (!descriptionMatch) return false;
    }
    
    // Then apply status filter
    const lineMatches = matches[line.id] || [];
    switch (lineFilter) {
      case 'pending':
        return line.status === 'pending' && lineMatches.length === 0;
      case 'with-suggestion':
        return line.status === 'pending' && lineMatches.length > 0;
      case 'matched':
        return line.status === 'matched';
      case 'created':
        return line.status === 'created';
      case 'ignored':
        return line.status === 'ignored';
      default:
        return true;
    }
  });

  // Count lines by filter type for badges
  const lineCounts = {
    all: lines.length,
    pending: lines.filter(l => l.status === 'pending' && (matches[l.id] || []).length === 0).length,
    'with-suggestion': lines.filter(l => l.status === 'pending' && (matches[l.id] || []).length > 0).length,
    matched: lines.filter(l => l.status === 'matched').length,
    created: lines.filter(l => l.status === 'created').length,
    ignored: lines.filter(l => l.status === 'ignored').length,
  };

  // Calculate progress
  const totalLines = lines.length;
  const processedLines = lines.filter((l) => l.status !== 'pending').length;
  const progressPercent = totalLines > 0 ? Math.round((processedLines / totalLines) * 100) : 0;

  // Get matched operation for a line
  const getMatchedOperation = (line: ReconciliationLine): Operation | undefined => {
    if (line.matchedOperationId) {
      return operations.find((op) => op.id === line.matchedOperationId);
    }
    return undefined;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container mx-auto px-4 py-6">
        <Card className="p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('reconciliation.sessionNotFound', 'Session non trouvée')}
          </p>
          <Button variant="primary" onClick={() => navigate('/reconciliation')} className="mt-4">
            {t('reconciliation.backToList', 'Retour à la liste')}
          </Button>
        </Card>
      </div>
    );
  }

  // Show wizard as full page replacement when open
  if (showImportWizard) {
    return (
      <div className="-m-3 sm:-m-4 lg:-m-6 -mb-[100px] sm:-mb-[100px] lg:-mb-[100px] flex-1 flex flex-col overflow-hidden min-h-[calc(100vh-4rem)]">
        <ReconciliationImportWizard
          isOpen={showImportWizard}
          onClose={() => setShowImportWizard(false)}
          onImport={handleImportLines}
          sessionName={session?.name}
          accountName={accountName}
        />
      </div>
    );
  }

  // Show PDF import as full page replacement when open
  if (showPDFImport) {
    return (
      <div className="-m-3 sm:-m-4 lg:-m-6 -mb-[100px] sm:-mb-[100px] lg:-mb-[100px] flex-1 flex flex-col overflow-hidden min-h-[calc(100vh-4rem)]">
        <PDFImportPanel
          onImport={handleImportPDFLines}
          onCancel={() => setShowPDFImport(false)}
          sessionName={session?.name}
          accountName={accountName}
        />
      </div>
    );
  }

  return (
    <div className="-m-3 sm:-m-4 lg:-m-6 -mb-[100px] sm:-mb-[100px] lg:-mb-[100px] flex-1 flex flex-col overflow-hidden min-h-[calc(100vh-4rem)]">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/reconciliation')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {session.name}
                </h1>
                {accountName && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    • {accountName}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {session.startDate && session.endDate
                  ? `${formatDate(session.startDate)} - ${formatDate(session.endDate)}`
                  : t('reconciliation.noDateRange', 'Importez un fichier CSV')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Progress */}
            {totalLines > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 transition-all"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {processedLines}/{totalLines}
                </span>
              </div>
            )}

            {/* Settings button */}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
              disabled={session.status !== 'in_progress'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </Button>

            {session.status === 'in_progress' && processedLines === totalLines && totalLines > 0 && (
              <Button variant="primary" size="sm" onClick={() => setShowCompleteModal(true)}>
                {t('reconciliation.completeSession', 'Terminer')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={() => setError(null)}
            className="text-xs text-red-600 dark:text-red-400 underline mt-1"
          >
            {t('common.dismiss', 'Fermer')}
          </button>
        </div>
      )}

      {/* Main content - Split view */}
      {lines.length === 0 ? (
        <div className="flex-1 p-4">
          <Card className="p-6 max-w-xl">
            <div className="flex items-start gap-4">
              <div className="shrink-0 p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <svg
                  className="h-6 w-6 text-primary-600 dark:text-primary-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-medium text-gray-900 dark:text-white">
                  {t('reconciliation.importYourFile', 'Importez votre relevé bancaire')}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t(
                    'reconciliation.importHelpShort',
                    'Format attendu : date, description, montant'
                  )}
                </p>
                
                {/* Import options */}
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  {/* CSV Import */}
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowImportWizard(true)}
                    disabled={isImporting}
                    className="flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t('reconciliation.importCSV', 'Importer CSV')}
                  </Button>
                  
                  {/* PDF Import (AI-powered) */}
                  {pdfExtractionStatus?.available ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => setShowPDFImport(true)}
                      disabled={isImporting}
                      className="flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {t('reconciliation.importPDF', 'Importer PDF')}
                      <span className="ml-1 px-1.5 py-0.5 text-[0.65rem] rounded bg-white/20">IA</span>
                    </Button>
                  ) : (
                    <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {pdfExtractionStatus?.reason === 'ai_disabled' 
                        ? t('reconciliation.pdfRequiresAI', 'Import PDF : activez l\'IA')
                        : pdfExtractionStatus?.reason === 'unsupported_provider'
                        ? t('reconciliation.pdfRequiresOpenAI', 'Import PDF : utilisez OpenAI ou Anthropic')
                        : t('reconciliation.pdfUnavailable', 'Import PDF non disponible')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      ) : (
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden min-h-0">
          {/* Mobile tabs */}
          <div className="md:hidden flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <button
              onClick={() => setMobileTab('lines')}
              className={`flex-1 py-2 px-2 text-xs font-medium transition-colors ${
                mobileTab === 'lines'
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-white dark:bg-gray-900'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {t('reconciliation.bankLines', 'Relevé')} ({filteredLines.length})
            </button>
            <button
              onClick={() => setMobileTab('operations')}
              className={`flex-1 py-2 px-2 text-xs font-medium transition-colors ${
                mobileTab === 'operations'
                  ? 'text-primary-600 border-b-2 border-primary-600 bg-white dark:bg-gray-900'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {t('reconciliation.accountOperations', 'Opérations')} ({operations.length})
            </button>
          </div>

          {/* Left panel - CSV Lines */}
          <div className={`${mobileTab === 'lines' ? 'flex' : 'hidden'} md:flex w-full md:w-1/2 md:border-r border-gray-200 dark:border-gray-700 flex-col min-h-0`}>
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <div className="hidden md:flex items-center justify-between mb-2">
                <h2 className="font-medium text-gray-900 dark:text-white">
                  {t('reconciliation.bankLines', 'Lignes du relevé')}
                </h2>
                <span className="text-sm text-gray-500">{filteredLines.length}/{lines.length}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setLineFilter('all')}
                  className={`px-1.5 py-0.5 sm:px-2 text-[0.625rem] sm:text-[0.6875rem] rounded-full transition-colors ${
                    lineFilter === 'all'
                      ? 'bg-gray-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  <span className="sm:hidden !text-[0.625rem]">{lineFilter === 'all' ? `${t('reconciliation.filter.all', 'Toutes')} (${lineCounts.all})` : lineCounts.all}</span>
                  <span className="hidden sm:inline !text-[0.6875rem]">{t('reconciliation.filter.all', 'Toutes')} ({lineCounts.all})</span>
                </button>
                <button
                  onClick={() => setLineFilter('pending')}
                  className={`px-1.5 py-0.5 sm:px-2 text-[0.625rem] sm:text-[0.6875rem] rounded-full transition-colors ${
                    lineFilter === 'pending'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                  }`}
                >
                  <span className="sm:hidden !text-[0.625rem]">{lineFilter === 'pending' ? `${t('reconciliation.filter.pending', 'À traiter')} (${lineCounts.pending})` : `⏳${lineCounts.pending}`}</span>
                  <span className="hidden sm:inline !text-[0.6875rem]">{t('reconciliation.filter.pending', 'À traiter')} ({lineCounts.pending})</span>
                </button>
                <button
                  onClick={() => setLineFilter('with-suggestion')}
                  className={`px-1.5 py-0.5 sm:px-2 text-[0.625rem] sm:text-[0.6875rem] rounded-full transition-colors ${
                    lineFilter === 'with-suggestion'
                      ? 'bg-purple-500 text-white'
                      : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50'
                  }`}
                >
                  <span className="sm:hidden !text-[0.625rem]">{lineFilter === 'with-suggestion' ? `${t('reconciliation.filter.withSuggestion', 'Avec sugg.')} (${lineCounts['with-suggestion']})` : `💡${lineCounts['with-suggestion']}`}</span>
                  <span className="hidden sm:inline !text-[0.6875rem]">{t('reconciliation.filter.withSuggestion', 'Avec sugg.')} ({lineCounts['with-suggestion']})</span>
                </button>
                <button
                  onClick={() => setLineFilter('matched')}
                  className={`px-1.5 py-0.5 sm:px-2 text-[0.625rem] sm:text-[0.6875rem] rounded-full transition-colors ${
                    lineFilter === 'matched'
                      ? 'bg-green-500 text-white'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                  }`}
                >
                  <span className="sm:hidden !text-[0.625rem]">{lineFilter === 'matched' ? `${t('reconciliation.filter.matched', 'Rapprochées')} (${lineCounts.matched})` : `✓${lineCounts.matched}`}</span>
                  <span className="hidden sm:inline !text-[0.6875rem]">{t('reconciliation.filter.matched', 'Rapprochées')} ({lineCounts.matched})</span>
                </button>
                <button
                  onClick={() => setLineFilter('created')}
                  className={`px-1.5 py-0.5 sm:px-2 text-[0.625rem] sm:text-[0.6875rem] rounded-full transition-colors ${
                    lineFilter === 'created'
                      ? 'bg-blue-500 text-white'
                      : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50'
                  }`}
                >
                  <span className="sm:hidden !text-[0.625rem]">{lineFilter === 'created' ? `${t('reconciliation.filter.created', 'Nouvelles')} (${lineCounts.created})` : `+${lineCounts.created}`}</span>
                  <span className="hidden sm:inline !text-[0.6875rem]">{t('reconciliation.filter.created', 'Nouvelles')} ({lineCounts.created})</span>
                </button>
                <button
                  onClick={() => setLineFilter('ignored')}
                  className={`px-1.5 py-0.5 sm:px-2 text-[0.625rem] sm:text-[0.6875rem] rounded-full transition-colors ${
                    lineFilter === 'ignored'
                      ? 'bg-gray-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <span className="sm:hidden !text-[0.625rem]">{lineFilter === 'ignored' ? `${t('reconciliation.filter.ignored', 'Ignorées')} (${lineCounts.ignored})` : `✗${lineCounts.ignored}`}</span>
                  <span className="hidden sm:inline !text-[0.6875rem]">{t('reconciliation.filter.ignored', 'Ignorées')} ({lineCounts.ignored})</span>
                </button>
              </div>
              {/* Search input */}
              <div className="mt-2 relative">
                <input
                  type="text"
                  value={lineSearchQuery}
                  onChange={(e) => setLineSearchQuery(e.target.value)}
                  placeholder={t('reconciliation.searchPlaceholder', 'Rechercher dans les libellés...')}
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {lineSearchQuery && (
                  <button
                    onClick={() => setLineSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-1.5 sm:p-2 space-y-1.5 sm:space-y-2">
              {[...filteredLines].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).map((line) => {
                const matchedOp = getMatchedOperation(line);
                return (
                  <div
                    key={line.id}
                    onClick={() => {
                      if (line.status === 'pending') {
                        // Toggle selection: deselect if already selected
                        const newSelection = selectedLineId === line.id ? null : line.id;
                        setSelectedLineId(newSelection);
                        // On mobile, switch to operations tab when selecting a line
                        if (newSelection && window.innerWidth < 768) {
                          setMobileTab('operations');
                        }
                      }
                    }}
                    className={`
                      p-1.5 sm:p-3 rounded-lg border-l-4 cursor-pointer transition-all
                      ${LINE_STATUS_COLORS[line.status]}
                      ${selectedLineId === line.id ? 'ring-2 ring-primary-500' : ''}
                      ${line.status === 'pending' ? 'hover:shadow-md' : 'opacity-75'}
                    `}
                  >
                    {/* First row: date, amount, badges/actions */}
                    <div className="flex items-center justify-between gap-1 sm:gap-2">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                          {formatDate(line.date)}
                        </span>
                        <span
                          className={`text-xs sm:text-sm font-bold whitespace-nowrap ${
                            line.amount >= 0
                              ? 'text-green-600 dark:text-green-400'
                              : 'text-red-600 dark:text-red-400'
                          }`}
                        >
                          {formatCurrency(line.amount)}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                        {line.status === 'pending' && matches[line.id]?.length ? (
                          <span className="text-[10px] sm:text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 px-1 sm:px-1.5 py-0.5 rounded-full">
                            {matches[line.id]?.length}💡
                          </span>
                        ) : null}
                        {line.status !== 'pending' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReset(line.id);
                            }}
                            className="text-[10px] sm:text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-0.5 sm:px-1"
                            disabled={session.status !== 'in_progress'}
                          >
                            {t('reconciliation.reset', 'Annuler')}
                          </button>
                        )}
                        {line.status === 'pending' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleIgnore(line.id);
                            }}
                            className="text-[10px] sm:text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-0.5 sm:px-1"
                            disabled={session.status !== 'in_progress'}
                          >
                            {t('reconciliation.ignore', 'Ignorer')}
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Second row: description */}
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                      {line.description}
                    </p>
                    {/* Third row: matched operation info */}
                    {matchedOp && (
                      <p className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 truncate">
                        ✓ {matchedOp.counterparty?.name || matchedOp.comment}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right panel - Operations */}
          <div className={`${mobileTab === 'operations' ? 'flex' : 'hidden'} md:flex w-full md:w-1/2 flex-col min-h-0`}>
            <div className="hidden md:block px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-medium text-gray-900 dark:text-white">
                  {t('reconciliation.accountOperations', 'Opérations du compte')}
                </h2>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {operations.filter(op => {
                    const isMatchedToLine = lines.some((l) => l.matchedOperationId === op.id);
                    if (operationFilter === 'reconciled') return isMatchedToLine;
                    if (operationFilter === 'not-reconciled') return !isMatchedToLine;
                    return true;
                  }).length} {t('reconciliation.operations', 'opérations')}
                </span>
              </div>
              {/* Operation status filter */}
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() => setOperationFilter('all')}
                  className={`px-2 py-0.5 text-[0.6875rem] rounded-full transition-colors ${
                    operationFilter === 'all'
                      ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('reconciliation.filterAll', 'Toutes')}
                </button>
                <button
                  onClick={() => setOperationFilter('not-reconciled')}
                  className={`px-2 py-0.5 text-[0.6875rem] rounded-full transition-colors ${
                    operationFilter === 'not-reconciled'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('reconciliation.filterNotReconciled', 'Non rapprochées')}
                </button>
                <button
                  onClick={() => setOperationFilter('reconciled')}
                  className={`px-2 py-0.5 text-[0.6875rem] rounded-full transition-colors ${
                    operationFilter === 'reconciled'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {t('reconciliation.filterReconciled', 'Rapprochées')}
                </button>
              </div>
            </div>

            {/* Mobile: Back button when line selected */}
            {selectedLine && (
              <div className="md:hidden px-2 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => {
                    setSelectedLineId(null);
                    setMobileTab('lines');
                  }}
                  className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  {t('common.back', 'Retour')}
                </button>
                <div className="text-[10px] text-gray-500">
                  {formatDate(selectedLine.date)} • {formatCurrency(selectedLine.amount, 'EUR')}
                </div>
              </div>
            )}

            {/* Quick actions when line selected - always show create/ignore options */}
            {selectedLine && selectedLine.status === 'pending' && session.status === 'in_progress' && (
              <div className={`px-2 py-1.5 sm:px-3 sm:py-2 border-b ${selectedLineMatches.length === 0 ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'}`}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-1 sm:gap-2">
                  <span className={`text-[10px] sm:text-sm hidden sm:block ${selectedLineMatches.length === 0 ? 'text-yellow-800 dark:text-yellow-200' : 'text-blue-800 dark:text-blue-200'}`}>
                    {selectedLineMatches.length === 0 
                      ? t('reconciliation.noMatchFound', 'Aucune correspondance suggérée')
                      : t('reconciliation.suggestionFound', `${selectedLineMatches.length} suggestion(s) - ou créer une nouvelle opération`)}
                  </span>
                  <div className="flex gap-1 sm:gap-2 w-full sm:w-auto">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-1 sm:flex-none text-xs sm:text-sm py-1 sm:py-2"
                      onClick={() => {
                        // Extract potential counterparty name from description
                        // Common patterns: "CARTE X7281 01/08 CINEVILLE" -> "CINEVILLE"
                        // "VIR SEPA DE: EMPLOYEUR" -> "EMPLOYEUR"
                        const desc = selectedLine.description;
                        let counterpartyName = '';
                        
                        // Try to extract name from common patterns
                        const patterns = [
                          /CARTE\s+\S+\s+\d{2}\/\d{2}\s+(.+?)(?:\s+\d|IOPD|$)/i,  // CARTE X7281 01/08 NAME
                          /VIR\s+(?:SEPA\s+)?(?:DE|POUR)[:\s]+(.+?)(?:\s+REF|$)/i, // VIR SEPA DE: NAME
                          /PRLV\s+(?:SEPA\s+)?(.+?)(?:\s+ID:|$)/i,                 // PRLV SEPA NAME
                          /CHQ\s+\d+\s+(.+)/i,                                      // CHQ 123456 NAME
                        ];
                        
                        for (const pattern of patterns) {
                          const match = desc.match(pattern);
                          if (match && match[1]) {
                            counterpartyName = match[1].trim();
                            break;
                          }
                        }
                        
                        // If no pattern matched, use first part of description
                        if (!counterpartyName && desc.length > 0) {
                          counterpartyName = desc.split(/\s{2,}|\d{10,}/)[0]?.trim() || '';
                        }

                        navigate(`/accounts/${session.accountId}/operations/new`, {
                          state: {
                            prefill: {
                              paymentDate: selectedLine.date,
                              amount: Math.abs(selectedLine.amount),
                              direction: selectedLine.amount < 0 ? 'debit' : 'credit',
                              reference: selectedLine.description,
                              counterpartyName: counterpartyName,
                            },
                            reconciliation: {
                              sessionId: session.id,
                              lineId: selectedLine.id,
                            },
                            returnTo: `/reconciliation/${session.id}`,
                          },
                        });
                      }}
                    >
                      {t('reconciliation.createOperation', 'Créer opération')}
                    </Button>
                    <Button variant="secondary" size="sm" className="flex-1 sm:flex-none text-xs sm:text-sm py-1 sm:py-2" onClick={() => handleIgnore(selectedLine.id)}>
                      {t('reconciliation.ignore', 'Ignorer')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-1.5 sm:p-2 space-y-1 sm:space-y-2">
              {operations.length > 0 ? (
                [...operations]
                .filter((op) => {
                  // Apply operation filter based on whether it's matched to a line in this session
                  const isMatchedToLine = lines.some((l) => l.matchedOperationId === op.id);
                  if (operationFilter === 'reconciled') return isMatchedToLine;
                  if (operationFilter === 'not-reconciled') return !isMatchedToLine;
                  return true;
                })
                .sort((a, b) => {
                  // Sort by accounting date if available, otherwise by payment date
                  const dateA = a.accountingDate || a.paymentDate;
                  const dateB = b.accountingDate || b.paymentDate;
                  return new Date(dateB).getTime() - new Date(dateA).getTime();
                }).map((operation) => {
                  // Check if this operation is a suggested match for the selected line
                  const matchInfo = selectedLineId
                    ? selectedLineMatches.find((m) => m.operationId === operation.id)
                    : null;
                  // Check if operation is already matched to another line
                  const isAlreadyMatched = lines.some(
                    (l) => l.matchedOperationId === operation.id
                  );

                  return (
                    <div
                      key={operation.id}
                      ref={(el) => { operationRefs.current[operation.id] = el; }}
                      className={`p-1.5 sm:p-3 rounded-lg border transition-all ${
                        matchInfo
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 ring-2 ring-primary-500/50'
                          : isAlreadyMatched
                          ? 'border-green-300 bg-green-50 dark:bg-green-900/20 opacity-60'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-1 sm:gap-2">
                        <div className="flex-1 min-w-0">
                          {/* First row: date, amount, status badge */}
                          <div className="flex items-center gap-1 sm:gap-2">
                            <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                              {formatDate(operation.accountingDate || operation.paymentDate)}
                            </span>
                            <span
                              className={`text-xs sm:text-sm font-bold whitespace-nowrap ${
                                operation.direction === 'credit'
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              {formatCurrency(
                                operation.direction === 'credit'
                                  ? operation.amount
                                  : -operation.amount
                              )}
                            </span>
                            {isAlreadyMatched && (
                              <span className="text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 shrink-0">
                                ✓
                              </span>
                            )}
                            {matchInfo && (
                              <span
                                className={`text-[10px] sm:text-xs px-1 sm:px-1.5 py-0.5 rounded-full shrink-0 ${
                                  matchInfo.score >= 0.8
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : matchInfo.score >= 0.6
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                                }`}
                              >
                                {Math.round(matchInfo.score * 100)}%
                              </span>
                            )}
                          </div>
                          {/* Second row: description */}
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate">
                            {operation.counterparty?.name || operation.comment || '-'}
                          </p>
                          {/* Third row (desktop only): match details */}
                          {matchInfo && (
                            <div className="hidden sm:flex gap-2 mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {matchInfo.dateMatch ? (
                                <span className="text-green-600">✓ Date exacte</span>
                              ) : (
                                <span>±{matchInfo.dateDifferenceDays}j</span>
                              )}
                              {matchInfo.descriptionSimilarity > 0.5 && (
                                <span className="text-green-600">
                                  ✓ {Math.round(matchInfo.descriptionSimilarity * 100)}% sim.
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Right side: eye icon + action buttons */}
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                          {/* Eye icon - always visible */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOperationForDetails(operation);
                              setShowOperationDetails(true);
                            }}
                            className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            title={t('reconciliation.viewDetails', 'Voir les détails')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          {/* Action buttons */}
                          {selectedLine && !isAlreadyMatched && session.status === 'in_progress' && (
                            <div className="flex gap-1">
                              {/* Show "Rapprocher" button if high match score OR if operation is already reconciled */}
                              {((matchInfo && matchInfo.score >= 0.7) || operation.status === 'reconciled') && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={() => {
                                    // Direct reconciliation without popup - set status to reconciled
                                    handleMatch(selectedLine.id, operation.id, false);
                                  }}
                                >
                                  {t('reconciliation.reconcile', 'Rapprocher')}
                                </Button>
                              )}
                              <Button
                                variant={(matchInfo && matchInfo.score >= 0.7) || operation.status === 'reconciled' ? 'secondary' : 'primary'}
                                size="sm"
                                onClick={() => initiateMatch(selectedLine.id, operation.id)}
                              >
                                {t('reconciliation.match', 'Pointer')}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>{t('reconciliation.noOperations', 'Aucune opération dans cette période')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        title={t('reconciliation.settingsTitle', 'Paramètres de la session')}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('reconciliation.dateTolerance', 'Tolérance de date (jours)')}
            </label>
            <input
              type="number"
              min={0}
              max={30}
              value={newTolerance}
              onChange={(e) => setNewTolerance(parseInt(e.target.value) || 0)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t(
                'reconciliation.dateToleranceHelp',
                "Nombre de jours d'écart accepté pour le rapprochement automatique"
              )}
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowSettingsModal(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveTolerance}
              disabled={isSavingSettings}
            >
              {isSavingSettings ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Complete Session Modal */}
      <Modal
        isOpen={showCompleteModal}
        onClose={() => setShowCompleteModal(false)}
        title={t('reconciliation.completeSessionTitle', 'Terminer la session')}
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t(
              'reconciliation.completeSessionConfirm',
              'Êtes-vous sûr de vouloir terminer cette session ? Toutes les lignes ont été traitées.'
            )}
          </p>
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {lines.filter((l) => l.status === 'matched').length}
                </div>
                <div className="text-xs text-gray-500">{t('reconciliation.matched', 'Pointées')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {lines.filter((l) => l.status === 'created').length}
                </div>
                <div className="text-xs text-gray-500">{t('reconciliation.created', 'Créées')}</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-600">
                  {lines.filter((l) => l.status === 'ignored').length}
                </div>
                <div className="text-xs text-gray-500">{t('reconciliation.ignored', 'Ignorées')}</div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setShowCompleteModal(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCompleteSession}
              disabled={isCompleting}
            >
              {isCompleting
                ? t('common.completing', 'Finalisation...')
                : t('reconciliation.completeSession', 'Terminer')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Date/Alias Adjustment Modal */}
      <Modal
        isOpen={showDateAdjustModal}
        onClose={() => {
          setShowDateAdjustModal(false);
          setPendingMatch(null);
        }}
        title={t('reconciliation.adjustmentTitle', 'Confirmation du rapprochement')}
      >
        {pendingMatch && (() => {
          const datesDiffer = pendingMatch.lineDate.split('T')[0] !== pendingMatch.operationDate.split('T')[0];
          const counterpartyDiffers = pendingMatch.counterpartyName && pendingMatch.counterpartyId &&
            !pendingMatch.lineDescription.toLowerCase().includes(pendingMatch.counterpartyName.toLowerCase());
          
          return (
            <div className="space-y-4">
              {/* Summary info */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 space-y-2 text-sm">
                {datesDiffer && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Dates :</span>
                    <span className="text-gray-900 dark:text-white">
                      {formatDate(pendingMatch.lineDate)} → {formatDate(pendingMatch.operationDate)}
                    </span>
                  </div>
                )}
                {counterpartyDiffers && (
                  <>
                    <div className="flex justify-between items-start">
                      <span className="text-gray-500">Relevé :</span>
                      <span className="text-gray-900 dark:text-white text-right max-w-[65%] break-words text-xs">
                        {pendingMatch.lineDescription}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tiers :</span>
                      <span className="text-gray-900 dark:text-white">{pendingMatch.counterpartyName}</span>
                    </div>
                  </>
                )}
              </div>

              {/* Checkbox options */}
              <div className="space-y-3">
                {datesDiffer && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shouldUpdateDate}
                      onChange={(e) => setShouldUpdateDate(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('reconciliation.updateDateOption', "Mettre à jour la date de l'opération")}
                      </span>
                      <p className="text-xs text-gray-500">
                        {formatDate(pendingMatch.operationDate)} → {formatDate(pendingMatch.lineDate)}
                      </p>
                    </div>
                  </label>
                )}
                
                {counterpartyDiffers && (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={shouldCreateAlias}
                      onChange={(e) => setShouldCreateAlias(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {t('reconciliation.createAliasOption', 'Créer un alias pour "{counterparty}"').replace('{counterparty}', pendingMatch.counterpartyName || '')}
                      </span>
                      <p className="text-xs text-gray-500">
                        {t('reconciliation.aliasHint', 'Améliore les suggestions futures')}
                      </p>
                    </div>
                  </label>
                )}
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => {
                    setShowDateAdjustModal(false);
                    setPendingMatch(null);
                  }}
                >
                  {t('common.cancel', 'Annuler')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    handleMatch(
                      pendingMatch.lineId, 
                      pendingMatch.operationId, 
                      shouldUpdateDate,
                      shouldCreateAlias ? pendingMatch.lineDescription : undefined
                    );
                  }}
                >
                  {t('reconciliation.confirm', 'Rapprocher')}
                </Button>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Operation Details Modal */}
      <Modal
        isOpen={showOperationDetails}
        onClose={() => {
          setShowOperationDetails(false);
          setSelectedOperationForDetails(null);
        }}
        title={t('reconciliation.operationDetails', 'Détails de l\'opération')}
      >
        {selectedOperationForDetails && (
          <div className="space-y-4">
            {/* Amount and direction */}
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('operations.amount', 'Montant')}
              </span>
              <span className={`text-lg font-bold ${
                selectedOperationForDetails.direction === 'credit'
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {formatCurrency(
                  selectedOperationForDetails.direction === 'credit'
                    ? selectedOperationForDetails.amount
                    : -selectedOperationForDetails.amount
                )}
              </span>
            </div>

            {/* Details grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Payment date */}
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.paymentDate', 'Date de paiement')}
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatDate(selectedOperationForDetails.paymentDate)}
                </p>
              </div>

              {/* Accounting date */}
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.accountingDate', 'Date comptable')}
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedOperationForDetails.accountingDate 
                    ? formatDate(selectedOperationForDetails.accountingDate)
                    : '-'}
                </p>
              </div>

              {/* Counterparty */}
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.counterparty', 'Tiers')}
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedOperationForDetails.counterparty?.name || '-'}
                </p>
              </div>

              {/* Category */}
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.category', 'Catégorie')}
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedOperationForDetails.categories?.[0]?.category?.name || '-'}
                </p>
              </div>

              {/* Status */}
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.form.status', 'Statut')}
                </span>
                <p className="text-sm font-medium">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${
                    selectedOperationForDetails.status === 'reconciled'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : selectedOperationForDetails.status === 'pointed'
                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {selectedOperationForDetails.status === 'reconciled' 
                      ? t('operations.status.reconciled', 'Rapproché')
                      : selectedOperationForDetails.status === 'pointed'
                      ? t('operations.status.pointed', 'Pointé')
                      : t('operations.status.unpointed', 'Non pointé')}
                  </span>
                </p>
              </div>

              {/* Payment method */}
              {selectedOperationForDetails.paymentMethod && (
                <div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {t('operations.paymentMethod', 'Mode de paiement')}
                  </span>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedOperationForDetails.paymentMethod.name}
                  </p>
                </div>
              )}
            </div>

            {/* Payment details (e.g., check number) */}
            {selectedOperationForDetails.paymentDetails && (
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.paymentDetails', 'Détails du paiement')}
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedOperationForDetails.paymentDetails}
                </p>
              </div>
            )}

            {/* Reference */}
            {selectedOperationForDetails.reference && (
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.reference', 'Référence')}
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white break-all">
                  {selectedOperationForDetails.reference}
                </p>
              </div>
            )}

            {/* Comment */}
            {selectedOperationForDetails.comment && (
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {t('operations.comment', 'Commentaire')}
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {selectedOperationForDetails.comment}
                </p>
              </div>
            )}

            {/* Close button */}
            <div className="flex justify-end pt-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowOperationDetails(false);
                  setSelectedOperationForDetails(null);
                }}
              >
                {t('common.close', 'Fermer')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default ReconciliationSessionPage;
